export const QR_REDIRECT_PATH_KEY = "qrRedirectPath";

/** Same-flow fallback so PublicRoute can still honor a just-consumed path. */
const CONSUMED_PATH_TTL_MS = 5_000;
let lastConsumedPath: string | null = null;
let lastConsumedAt = 0;

function readStoredQrRedirectPath(): string | null {
  try {
    const value = sessionStorage.getItem(QR_REDIRECT_PATH_KEY);
    if (value == null) {
      return null;
    }
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  } catch {
    return null;
  }
}

function readConsumedQrRedirectPath(): string | null {
  if (!lastConsumedPath) {
    return null;
  }
  if (Date.now() - lastConsumedAt > CONSUMED_PATH_TTL_MS) {
    lastConsumedPath = null;
    return null;
  }
  return lastConsumedPath;
}

/**
 * Deep-link stored by QR landings (material, batch, stock, dispatch-guide).
 * The value includes pathname and search (do not strip the query string).
 */
export function readQrRedirectPath(): string | null {
  return readStoredQrRedirectPath() ?? readConsumedQrRedirectPath();
}

export function consumeQrRedirectPath(): string | null {
  const path = readStoredQrRedirectPath() ?? readConsumedQrRedirectPath();
  if (!path) {
    return null;
  }
  try {
    sessionStorage.removeItem(QR_REDIRECT_PATH_KEY);
  } catch {
    // sessionStorage may be unavailable
  }
  lastConsumedPath = path;
  lastConsumedAt = Date.now();
  return path;
}

/**
 * True when the stored QR path includes a tenant subscription in the query.
 * Stock QR (`/stock/qr/storage/:id`, `/stock/qr/bin/:id`) has no subscriptionId
 * and must not skip the subscription/company selector after login.
 */
export function qrRedirectPathCarriesTenant(path: string): boolean {
  const qIndex = path.indexOf("?");
  if (qIndex < 0) return false;
  const qs = new URLSearchParams(path.slice(qIndex + 1));
  return Boolean(qs.get("subscriptionId")?.trim());
}

/** Test helper: clears in-memory fallback without waiting for TTL. */
export function resetQrRedirectPathMemory(): void {
  lastConsumedPath = null;
  lastConsumedAt = 0;
}
